/* eslint-disable no-underscore-dangle, camelcase */
import React, {
  useState,
  useEffect,
  useCallback,
} from 'react';
import {
  shallowEqual,
  useDispatch,
  useSelector,
} from 'react-redux';
import i18n from 'i18next';
import {
  toast,
} from 'react-toastify';
import {
  useHistory,
} from 'react-router-dom';

import {
  coreActions,
  coreSelectors,
  ResourceType,
} from '@gymcloud/core';

import {
  useProgram,
} from 'web-hooks';
import {
  uiActions,
} from 'web-actions';

import {
  Button,
  Author,
  Tooltip,
  MoveToFolder,
  CreatableEntityButton,
} from 'web-components';

import {
  Flex,
} from 'web-styled';

import * as S from './styled';

type Props = {
  mode?: 'all' | 'standard',
  readOnly: boolean,
  programId: number,
  resourceType: ResourceType,
};

const ProgramHeader = ({
  mode,
  readOnly,
  programId,
  resourceType,
}: Props) => {
  const isPersonal = resourceType === 'personalPrograms';
  const [isSaveModal, setSaveModal] = useState(false);
  const history = useHistory();
  const mapState = useCallback((state) => {
    const program = coreSelectors.getResourceById(
      resourceType,
      programId,
    )(state);
    return {
      isPro: coreSelectors.getProfile('is_pro')(state),
      program,
      programAuthor: coreSelectors.getResourceById(
        'clients',
        program?.author_id,
      )(state),
      programWorkouts: coreSelectors.getResourceMappedList(
        'programWorkouts',
        `program_${programId}`,
      )(state),
      workoutOptions: coreSelectors.getProgamWorkoutOptions(
        isPersonal ? program.person_id : 'me',
      )(state),
      weeks: coreSelectors.getProgamWeeks(
        programId,
        resourceType,
      )(state).filter(item => item.id > 0),
    };
  }, [programId]);

  const {
    isPro,
    weeks,
    program,
    programAuthor,
    workoutOptions,
    programWorkouts,
  } = useSelector(mapState, shallowEqual);
  const dispatch = useDispatch();
  const {
    requestAddWeek,
    requestAddWorkout,
    requestSaveAsTemplate,
  } = useProgram({ programId, resourceType });

  useEffect(() => {
    if (program?.person_id) {
      dispatch(
        coreActions.requestClientPersonalEntity({
          qs: {
            status: 'active',
          },
          clientId: program.person_id,
          resourceType: 'personalWorkouts',
        }),
      );
    }
  }, [program?.person_id]);

  const handlePrint = () => {
    window.print();
  };

  const handleEdit = () => {
    if (readOnly) {
      const onConfirm = () => {
        history.push(`/personal_programs/${program.id}/edit/workouts`);
      };

      dispatch(
        uiActions.confirmModal({
          modal: {
            title: `Edit ${program?.name}`,
            bodyText: 'Changes will be saved immediately as soon as you move to the next field.',
            confirmText: 'Update',
          },
          onConfirm,
        }),
      );
    } else {
      history.push(`/personal_programs/${program.id}/workouts`);
    }
  };

  return (
    <S.Header>
      <Flex
        align="center"
        spacing={30}
        grow={1}
      >
        {readOnly ? (
          <S.CounterWrapper align="center" spacing={10}>
            <S.Counter>
              {weeks.length || 0}
            </S.Counter>
            <S.CounterName>WEEKS</S.CounterName>
          </S.CounterWrapper>
        ) : (
          <S.CounterWrapper justify="center" align="center" spacing={10}>
            <S.Counter>
              {weeks.length || 0}
            </S.Counter>
            <S.CounterName>WEEKS</S.CounterName>
            <Tooltip
              body={i18n.t(`tooltip.program.${mode === 'standard'
                ? 'add.weeks'
                : 'standard.view'}`)}
              preferPlace="right"
            >
              <S.AddButton
                role="button"
                onClick={requestAddWeek}
                style={{
                  cursor: mode === 'all' ? 'default' : 'pointer',
                }}
              >
                <S.PlusCircle />
              </S.AddButton>
            </Tooltip>
          </S.CounterWrapper>
        )}
        <S.CounterWrapper
          align="center"
          spacing={10}
          grow={1}
        >
          <S.Counter>
            {programWorkouts.length}
          </S.Counter>
          <S.CounterName>{i18n.t('workouts')}</S.CounterName>
          {!readOnly && (
            <CreatableEntityButton
              buttonProps={{
                appearance: 'empty',
                style: {
                  cursor: mode === 'all' ? 'default' : 'pointer',
                  opacity: 0.6,
                  padding: 0,
                  marginTop: 2,
                },
              }}
              options={workoutOptions}
              buttonLabel={(
                <S.PlusCircle />
              )}
              placeholder={i18n.t('search.placeholder.workoutTemplates')}
              isDisabled={mode === 'all'}
              tooltip={i18n.t(`tooltip.program.${mode === 'standard'
                ? 'add.workouts'
                : 'standard.view'}`)}
              menuPlacement="bottom"
              onRequestAdd={requestAddWorkout}
              onRequestAddNew={requestAddWorkout}
            />
          )}
        </S.CounterWrapper>
      </Flex>
      {isPersonal ? (
        <Flex
          align="center"
          spacing={10}
          noPrint
        >
          {readOnly && (
            <Button
              ghost
              appearance="primary"
              onClick={handlePrint}
            >
              Print
            </Button>
          )}
          {isPro && (
            <>
              <Button
                ghost
                appearance="primary"
                onClick={handleEdit}
              >
                {readOnly ? 'Edit' : 'Save'}
              </Button>
              <Button
                ghost
                appearance="primary"
                onClick={() => setSaveModal(true)}
              >
                Save as Template
              </Button>
            </>
          )}
        </Flex>
      ) : (
        <Author
          fullName={program?.author_full_name}
          avatar={programAuthor?.user_profile?.avatar?.thumb?.url}
        />
      )}
      <MoveToFolder
        modalState={{
          isOpen: isSaveModal,
          title: 'Save to Folder',
          helper: 'Select folder to save Program template to',
          buttonText: 'Save',
          folderName: 'Program Templates',
        }}
        onSubmit={(values, actions) => {
          const callbacks = {
            onSuccess: () => {
              actions.setSubmitting(false);
              setSaveModal(false);
              toast.success(
                'Program was saved to your library',
              );
            },
          };
          requestSaveAsTemplate(values, callbacks);
        }}
        onRequestClose={() => setSaveModal(false)}
      />
    </S.Header>
  );
};

ProgramHeader.defaultProps = {
  mode: 'standard',
};

export default ProgramHeader;
